import { Cancel01Icon } from '@hugeicons/core-free-icons';
import React, { useEffect, type ReactNode } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Modal as PaperModal, Portal } from 'react-native-paper';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import type { WithResponsive } from '../utils/responsivity';
import { isNative, useMediaQueries } from '../utils/responsivity';
import { HeaderButton } from './HeaderButton';
import { TextBold } from './StyledText';

export type ModalSize = 'small' | 'medium' | 'large';

const MODAL_SIZES: Record<
  ModalSize,
  { maxWidth: number; height: number | 'auto'; padding: number }
> = {
  small: { maxWidth: 400, height: 'auto', padding: 16 },
  medium: { maxWidth: 600, height: 'auto', padding: 24 },
  large: { maxWidth: 900, height: 766, padding: 24 },
};

export type ModalProps = {
  visible: boolean;
  hide: () => void;
  children: ReactNode;
  fullscreenOnBreakpoint?: boolean;
  modalStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  /** Custom header content. If provided, title and showCloseButton are ignored. */
  header?: ReactNode;
  /** Modal title - displayed centered in the header */
  title?: string;
  /** Whether to show the close button in the header. Defaults to true. */
  showCloseButton?: boolean;
  breakpoint?: 'mobile' | 'tablet';
  fadeIn?: boolean;
  fadeInDuration?: number;
  /** If true, renders a full-screen blocking backdrop behind the modal */
  blockScreen?: boolean;
  /** Size preset for the modal */
  size?: ModalSize;
  /** Enable keyboard avoiding behavior. Defaults to false. */
  keyboardAvoiding?: boolean;
};

export function Modal({
  visible,
  hide,
  children,
  fullscreenOnBreakpoint = true,
  modalStyle,
  contentStyle,
  header,
  title,
  showCloseButton = true,
  breakpoint = 'mobile',
  fadeIn = false,
  fadeInDuration = 300,
  blockScreen,
  size = 'large',
  keyboardAvoiding = false,
}: ModalProps) {
  const insets = useSafeAreaInsets();
  const { isMobile, isTablet } = useMediaQueries();
  const isBreakpoint = isMobile || (breakpoint === 'tablet' && isTablet);
  const sizeConfig = MODAL_SIZES[size];
  const styles = useDynamicStyles(createStyles, {
    fullscreenOnBreakpoint,
    isBreakpoint,
    insetTop: header ? 0 : insets.top,
    sizeConfig,
  });
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (fadeIn) {
      if (visible) {
        opacity.value = withTiming(1, { duration: fadeInDuration });
      } else {
        opacity.value = 0;
      }
    }
  }, [visible, fadeIn, fadeInDuration, opacity]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      flex: 1,
      opacity: opacity.value,
    };
  });

  const renderHeader = () => {
    // Custom header handles its own styling/padding
    if (header) {
      return header;
    }

    if (!title && !showCloseButton) {
      return null;
    }

    return (
      <View style={styles.defaultHeader}>
        {title && <TextBold style={styles.headerTitle}>{title}</TextBold>}
        {showCloseButton && (
          <View style={styles.closeButton}>
            <HeaderButton icon={Cancel01Icon} onPress={hide} size="small" />
          </View>
        )}
      </View>
    );
  };

  const content = keyboardAvoiding ? (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.keyboardAvoidingView}
    >
      {renderHeader()}
      <View style={[styles.content, contentStyle]}>{children}</View>
    </KeyboardAvoidingView>
  ) : (
    <>
      {renderHeader()}
      <View style={[styles.content, contentStyle]}>{children}</View>
    </>
  );

  return (
    <Portal>
      {blockScreen && visible && <View style={styles.blockingBackdrop} />}
      <PaperModal
        visible={visible}
        onDismiss={hide}
        contentContainerStyle={[styles.modalContent, modalStyle]}
        style={styles.modal}
      >
        {fadeIn ? (
          <Animated.View style={animatedStyle}>{content}</Animated.View>
        ) : (
          content
        )}
      </PaperModal>
    </Portal>
  );
}

const createStyles = ({
  fullscreenOnBreakpoint,
  isBreakpoint,
  insetTop,
  sizeConfig,
}: WithResponsive<
  ModalProps & {
    isBreakpoint: boolean;
    insetTop: number;
    sizeConfig: { maxWidth: number; height: number | 'auto'; padding: number };
  }
>) =>
  StyleSheet.create({
    blockingBackdrop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: Colors.purple0,
    },
    modal: {
      marginTop: isBreakpoint ? 0 : 40,
      marginBottom: isBreakpoint ? 0 : 40,
    },
    modalContent: {
      width: isBreakpoint ? (fullscreenOnBreakpoint ? '100%' : '80%') : '80%',
      height: isBreakpoint ? '100%' : (sizeConfig?.height ?? 766),
      maxHeight: '100%',
      borderRadius: isBreakpoint ? 0 : 16,
      maxWidth: sizeConfig?.maxWidth ?? 900,
      backgroundColor: Colors.purple0,
      marginLeft: isBreakpoint && !fullscreenOnBreakpoint ? 0 : 'auto',
      marginRight: 'auto',
      overflow: isBreakpoint ? 'visible' : 'hidden',
      shadowOpacity: 0,
      paddingTop: insetTop,
    },
    defaultHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: sizeConfig?.padding ?? 24,
      paddingTop: sizeConfig?.padding ?? 24,
      zIndex: 1,
    },
    headerTitle: {
      fontSize: 18,
      color: Colors.text,
      textAlign: 'center',
    },
    closeButton: {
      position: 'absolute',
      right: sizeConfig?.padding ?? 24,
      top: sizeConfig?.padding ?? 24,
    },
    keyboardAvoidingView: {
      flex: 1,
      marginBottom: isNative ? 12 : 0,
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      padding: sizeConfig?.padding ?? 24,
      flex: 1,
    },
  });
